import { useCallback, useEffect, useState } from "react";

/**
 * Two product modes:
 *  - "simple" (default for everyone)  → Home / Ask / Dashboards / Inbox + 3 collapsed advanced groups
 *  - "pro"                             → full sidebar (current 30+ entries)
 *
 * v1 persists choice in localStorage. v2 could lift this to a per-user
 * preference on the server so the choice survives device/browser.
 */
export type ViewMode = "simple" | "pro";

const STORAGE_KEY = "ligament.viewMode";

function readStored(): ViewMode {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "pro" ? "pro" : "simple";
  } catch {
    return "simple";
  }
}

export function useViewMode(): {
  mode: ViewMode;
  setMode: (m: ViewMode) => void;
  toggle: () => void;
} {
  const [mode, setModeState] = useState<ViewMode>(() => readStored());

  // React to changes from other tabs / windows
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setModeState(e.newValue === "pro" ? "pro" : "simple");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((m: ViewMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // ignore — storage might be disabled in private mode
    }
    setModeState(m);
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === "simple" ? "pro" : "simple");
  }, [mode, setMode]);

  return { mode, setMode, toggle };
}
